import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ActivityType, Lead, Stage } from '@figureit/shared';
import { DEFAULT_ACTIVITY_TYPES } from '@figureit/shared';
import { useQueryClient } from '@tanstack/react-query';
import { uploadAttachment, useAddActivity, useUpdateLead } from '../api/queries';
import { dictationHint, useDictation, useVoiceMemo, type VoiceMemo } from '../hooks/useSpeech';
import { fmtDate, today } from '../utils/format';
import {
  NEXT_STEP_CHIPS,
  addDays,
  appendDictation,
  fmtSeconds,
  hasOutcome,
  outcomesFor,
  toggleOutcome,
} from '../utils/quickLog';
import { Modal } from './ui/Modal';

/**
 * One-screen "what happened?" sheet. Built for thumbs: pick a type, tap an
 * outcome or two, dictate or type a line, set the next step with one tap and
 * save. A single Save writes the activity, the next action and (optionally)
 * a stage move, so a rep can document a call in under ten seconds.
 */
export function LogActivitySheet({
  lead,
  stages,
  types,
  initialTypeName,
  title,
  onClose,
  onStageChange,
}: {
  lead: Lead;
  stages: Stage[];
  types: ActivityType[];
  /** Pre-select this type (matched by name, case-insensitive). */
  initialTypeName?: string;
  title?: string;
  onClose: () => void;
  /** Delegated so Won/Lost can go through the reason prompt owned by the parent. */
  onStageChange: (stageId: string) => void;
}) {
  const qc = useQueryClient();
  const addAct = useAddActivity(lead.id);
  const update = useUpdateLead();

  // Fall back to the default type names if the org deleted all its types —
  // the API accepts a free-text typeName.
  const options = useMemo<{ key: string; id?: string; name: string; template?: string | null }[]>(
    () =>
      types.length
        ? types
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((t) => ({ key: t.id, id: t.id, name: t.name, template: t.template }))
        : DEFAULT_ACTIVITY_TYPES.map((t) => ({ key: t.name, name: t.name, template: t.template })),
    [types],
  );
  const initialKey =
    options.find((o) => o.name.toLowerCase() === (initialTypeName ?? '').toLowerCase())?.key ??
    options[0]?.key ??
    '';

  const [typeKey, setTypeKey] = useState(initialKey);
  const [date, setDate] = useState(today());
  const [summary, setSummary] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [stageId, setStageId] = useState(lead.stageId);
  const [memo, setMemo] = useState<VoiceMemo | null>(null);
  const [previewBroken, setPreviewBroken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const type = options.find((o) => o.key === typeKey);
  const outcomes = outcomesFor(type?.name);

  const dictation = useDictation((text) => setSummary((s) => appendDictation(s, text)));
  const voice = useVoiceMemo();

  // Release the preview blob URL when the memo is discarded or the sheet closes.
  useEffect(
    () => () => {
      if (memo) URL.revokeObjectURL(memo.url);
    },
    [memo],
  );

  const insertTemplate = () => {
    if (!type?.template) return;
    const expanded = type.template
      .replace(/\{contact\}/gi, lead.contactName ?? lead.company)
      .replace(/\{date\}/gi, fmtDate(today()));
    setSummary((s) => (s.trim() ? `${s.replace(/\s+$/, '')}\n${expanded}` : expanded));
    textRef.current?.focus();
  };

  const pickNextDate = (days: number) => {
    const d = addDays(days);
    setNextDate((cur) => (cur === d ? '' : d));
  };

  const toggleMemo = async () => {
    if (voice.recording) {
      const m = await voice.stop();
      if (m) {
        if (memo) URL.revokeObjectURL(memo.url);
        setPreviewBroken(false);
        setMemo(m);
      }
    } else {
      if (dictation.listening) dictation.stop();
      await voice.start();
    }
  };

  const canSave = !!(summary.trim() || memo) && !saving && !voice.recording;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      let text = summary.trim();
      if (memo) {
        const att = await uploadAttachment(lead.id, memo.file);
        qc.invalidateQueries({ queryKey: ['attachments', lead.id] });
        const line = `🎙 Voice memo (${fmtSeconds(memo.seconds)}) · ${att.fileName}`;
        text = text ? `${text}\n${line}` : line;
      }
      await addAct.mutateAsync({
        typeId: type?.id,
        typeName: type?.id ? undefined : type?.name ?? 'Note',
        date,
        summary: text,
      });
      const patch: { nextAction?: string | null; nextActionDate?: string | null } = {};
      if (nextAction.trim()) patch.nextAction = nextAction.trim();
      if (nextDate) {
        patch.nextActionDate = nextDate;
        if (!nextAction.trim()) patch.nextAction = lead.nextAction || 'Follow up';
      }
      if (Object.keys(patch).length) await update.mutateAsync({ id: lead.id, ...patch });
      if (stageId !== lead.stageId) onStageChange(stageId);
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Could not save. Check your connection and try again.');
      setSaving(false);
    }
  };

  const micError = dictation.error ?? voice.error;

  return (
    <Modal
      onClose={onClose}
      zIndex="z-[70]"
      title={title ?? `Log · ${lead.contactName || lead.company}`}
      footer={
        <>
          <button className="btn btn-secondary flex-1 sm:flex-none" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary flex-1 sm:flex-none"
            onClick={save}
            disabled={!canSave}
            data-testid="log-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {/* ---- Type ---- */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap pb-1">
        {options.map((o) => (
          <Chip key={o.key} active={o.key === typeKey} onClick={() => setTypeKey(o.key)}>
            {o.name}
          </Chip>
        ))}
      </div>

      {/* ---- Outcomes ---- */}
      {outcomes.length > 0 && (
        <div className="mt-3">
          <div className="text-[12px] text-text2 font-medium mb-1.5">Outcome</div>
          <div className="flex flex-wrap gap-2">
            {outcomes.map((o) => (
              <Chip
                key={o}
                small
                active={hasOutcome(summary, o)}
                onClick={() => setSummary((s) => toggleOutcome(s, o))}
              >
                {o}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {/* ---- Note ---- */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[12px] text-text2 font-medium" htmlFor="log-summary">
            Notes
          </label>
          {type?.template && (
            <button className="text-[12px] text-accent2" onClick={insertTemplate} type="button">
              Insert template
            </button>
          )}
        </div>
        <div className="relative">
          <textarea
            id="log-summary"
            ref={textRef}
            rows={4}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder={
              dictation.listening
                ? 'Listening…'
                : 'What happened? Tap an outcome above, dictate, or type…'
            }
            className={clsx(dictation.listening && 'ring-2 ring-red/60')}
          />
          {dictation.interim && (
            <div className="pointer-events-none absolute left-3 bottom-2 right-3 text-sm text-text3 truncate">
              {dictation.interim}
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          {dictation.supported && (
            <button
              type="button"
              className={clsx(
                'btn btn-sm',
                dictation.listening ? 'bg-red text-white' : 'btn-secondary',
              )}
              onClick={() => (dictation.listening ? dictation.stop() : dictation.start())}
              disabled={voice.recording}
              aria-pressed={dictation.listening}
            >
              {dictation.listening ? '■ Stop dictation' : '🎤 Dictate'}
            </button>
          )}
          {voice.supported && (
            <button
              type="button"
              className={clsx('btn btn-sm', voice.recording ? 'bg-red text-white' : 'btn-secondary')}
              onClick={toggleMemo}
              aria-pressed={voice.recording}
            >
              {voice.recording ? `■ Stop (${fmtSeconds(voice.seconds)})` : '🎙 Voice memo'}
            </button>
          )}
        </div>
        {micError && <div className="text-red text-xs mt-1.5">{micError}</div>}
        {!micError && dictationHint() && (
          <div className="text-text3 text-xs mt-1.5">{dictationHint()}</div>
        )}
        {memo && (
          <div className="mt-2 flex items-center gap-2 bg-surface2 border border-border rounded-md px-3 py-2">
            {previewBroken && (
              <span className="text-xs text-text2 flex-1">
                🎙 Recorded · preview not supported here, the memo is still saved
              </span>
            )}
            <audio
              controls
              src={memo.url}
              className="h-9 flex-1 min-w-0"
              preload="metadata"
              onError={() => setPreviewBroken(true)}
            />
            <span className="text-xs text-text3 flex-shrink-0">{fmtSeconds(memo.seconds)}</span>
            <button
              type="button"
              className="text-text3 hover:text-red px-2 -mr-2 text-base leading-none"
              aria-label="Discard voice memo"
              onClick={() => setMemo(null)}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* ---- Next step ---- */}
      <div className="mt-4">
        <label className="text-[12px] text-text2 font-medium mb-1.5 block" htmlFor="log-next">
          Next step
        </label>
        <input
          id="log-next"
          value={nextAction}
          onChange={(e) => setNextAction(e.target.value)}
          placeholder={lead.nextAction ? `Keep "${lead.nextAction}"` : 'e.g. Send proposal'}
          enterKeyHint="done"
          autoCapitalize="sentences"
        />
        <div className="flex gap-2 mt-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap items-center">
          {NEXT_STEP_CHIPS.map((c) => (
            <Chip
              key={c.label}
              small
              active={nextDate === addDays(c.days)}
              onClick={() => pickNextDate(c.days)}
            >
              {c.label}
            </Chip>
          ))}
          <input
            type="date"
            aria-label="Next step date"
            value={nextDate}
            onChange={(e) => setNextDate(e.target.value)}
            className="!w-auto !min-h-[40px] !py-1 text-sm flex-shrink-0"
          />
        </div>
      </div>

      {/* ---- Stage + date ---- */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className="text-[12px] text-text2 font-medium mb-1.5 block" htmlFor="log-stage">
            Stage
          </label>
          <select id="log-stage" value={stageId} onChange={(e) => setStageId(e.target.value)}>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[12px] text-text2 font-medium mb-1.5 block" htmlFor="log-date">
            Date
          </label>
          <input id="log-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      {error && <div className="text-red text-sm mt-3">{error}</div>}
    </Modal>
  );
}

function Chip({
  active,
  small,
  onClick,
  children,
}: {
  active?: boolean;
  small?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!active}
      className={clsx(
        'flex-shrink-0 rounded-full border font-medium transition-colors select-none',
        small ? 'px-3 min-h-[36px] text-[13px]' : 'px-4 min-h-[40px] text-sm',
        active
          ? 'bg-accent border-accent text-white'
          : 'bg-surface2 border-border text-text2 hover:text-text hover:bg-surface3',
      )}
    >
      {children}
    </button>
  );
}
